<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import {
        PageLoading,
        Button,
        SvelteTable,
        Pagination,
        NotificationModal,
        SearchSelect,
        CardLayout,
    } from "../../../Components/Common/index.svelte";

    import { isMobile } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        AustralianStates,
        pageOptions,
    } from "../../../Utils/utilEnums.svelte";

    import {
        getSearchSurgeonOptions,
        getText,
        getTitle,
        isObjectEmpty,
        replaceURL,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";
    import {
        addIcon,
        deleteIcon,
        proceduralSpecialtiesIds,
        SEARCH,
    } from "../../../Utils/utilConstants.svelte";

    import CreateSurgeonModal from "../../PatientEncounter/components/CreateSurgeonModal.svelte";

    export let accountId, params;

    let isGettingAccountDoctors = false,
        accountDoctors = [];

    let accountDoctorSearchResults = [],
        filters = { state: "VIC" },
        isSearchingSurgeons = false;

    let accountDoctorDetails = {},
        isModifyAccountDoctorModalOpen = false,
        modifyType = "",
        isModifyingAccountDoctor = false;

    let searchCommonSurgeons = false;

    let page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "name",
            title: "Name",
            headerClass: "text-left px-3 w-[30%] cursor-default rounded-tl-xl",
            class: "text-left pl-4 cursor-default",
            value: (v) => _getName(v),
        },
        {
            key: "speciality",
            title: "Speciality",
            headerClass: "text-left px-3 w-[60%] cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getText(v.speciality),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass:
                "text-left pl-3 pr-6 cursor-default rounded-tr-xl w-[2%]",
            class: "px-3 cursor-default",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    onMount(() => {
        if (params.query) _updateSearchParamsByQuery(params.query);

        loadAccountDoctors(page * pageSize, pageSize);
    });
 
    const loadAccountDoctors = async (page, size) => {
        isGettingAccountDoctors = true;

        await apiMethods.getMethod
            .getAccountDoctors(page, size, {
                "filter[doctorId]": accountId,
            })
            .then((res) => _handleAccountDoctors(res))
            .catch((err) => showException(err));

        isGettingAccountDoctors = false;
    };

    const handleFilterSelect = () => {
        if (filters && filters.name) loadAllSurgeons();
    };

    const handleDoctorSearch = async (text) => {
        if (!text || text.length < 2) {
            return [];
        }
        filters.name = text;

        if (searchCommonSurgeons)
            filters.specialitycode = proceduralSpecialtiesIds.join(",");
        else delete filters.specialitycode;

        loadAllSurgeons();
    };

    const loadAllSurgeons = async () => {
        isSearchingSurgeons = true;

        accountDoctorSearchResults = [];

        _initAccountDoctorDetails();

        await apiMethods.getMethod
            .getAllProviders(0, 999999, filters)
            .then((res) => _handleSearchSurgeons(res))
            .catch((err) => showException(err));

        isSearchingSurgeons = false;
    };

    const modifyAccountDoctor = async () => {
        isModifyingAccountDoctor = true;

        switch (modifyType) {
            // case "Create":
            //     await createSurgeon();
            //     break;
            case "Delete":
                await deleteSurgeon();
                break;
            default:
                break;
        }

        _closeModifyAccountDoctorModal();

        isModifyingAccountDoctor = false;
    };

    const createSurgeon = async () => {
        if (
            isObjectEmpty(accountDoctorDetails) ||
            !accountDoctorDetails.searchResult
        )
            return;

        const obj = {
            doctorId: accountId,
            providerUniqueId: accountDoctorDetails.searchResult.uniqueId,
        };

        filters = { state: "VIC" };

        await apiMethods.postMethod
            .createSurgeon(obj)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        "Failed to create surgeon, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await loadAccountDoctors(page * pageSize, pageSize);
            })
            .catch((err) => showException(err));
    };

    const deleteSurgeon = async () => {
        await apiMethods.deleteMethod
            .deleteAccountDoctor(accountDoctorDetails.id)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        "Failed to delete selected surgeon, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await loadAccountDoctors(page * pageSize, pageSize);
            })
            .catch((err) => showException(err));
    };

    const handleClickCell = (event, rowData, key) => {
        const { className } = event.target;

        if (key == "buttons") {
            if (className.includes("deleteIcon"))
                _openModifyAccountDoctorModal("Delete", rowData);
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAccountDoctors(Number(e.detail.firstDataIndex), pageSize);
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAccountDoctors(0, pageSize);
    };

    const updateURL = () => {
        replaceURL(
            `/Account/view/1/tab=accountDoctors&page=${page}&size=${pageSize}`,
        );
    };

    const getOptionLabel = (option) => _getName(option);

    const clearSelectedSurgeon = () => {
        accountDoctorDetails.searchResult = null;
    };

    // HELPER FUNCTIONS
    const _updateSearchParamsByQuery = (query) => {
        const arr = query.split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);
            }
        }
    };

    const _handleAccountDoctors = (res) => {
        if (!res || res.error)
            showError(
                "Failed to load account doctors, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            //filter account doctors by unique id
            const filtered = [];

            res.results.forEach((sur) => {
                sur.uniqueId = sur.uniqueId ?? sur.providerUniqueId;
                if (!filtered.some((filteredSurgeon) => filteredSurgeon.uniqueId === sur.uniqueId))
                    filtered.push(sur);
            });
            accountDoctors = [...filtered];
            count = filtered.length;
        }
    };

    const _handleSearchSurgeons = (res) => {
        if (!res || res.error)
            showError(
                "Failed to load surgeons, please refresh the page and try again and contact support if the issue persists.",
            );
        else accountDoctorSearchResults = _filterProviders(res.results);
    };

    const _filterProviders = (arr = []) => {
        if (!arr.length) return [];

        if (!accountDoctors.length) return getSearchSurgeonOptions(arr);

        const providerIds = accountDoctors.map(
            ({ providerUniqueId }) => providerUniqueId,
        );

        return getSearchSurgeonOptions(
            arr.filter(({ uniqueId }) => !providerIds.includes(uniqueId)),
        );
    };

    const _getName = (data) => {
        const { firstName, lastName, title, state } = data;

        return `${getTitle(title)} ${firstName} ${lastName}`
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Surgeon">
                        <i class="deleteIcon cursor-pointer ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openModifyAccountDoctorModal = (type, obj = {}) => {
        if (!type) return;

        modifyType = type;
        isModifyAccountDoctorModalOpen = true;

        switch (type) {
            case "Create":
                _initAccountDoctorDetails();
                break;
            case "Delete":
                _initAccountDoctorDetails(obj);
                break;
            default:
                break;
        }
    };

    const _closeModifyAccountDoctorModal = () => {
        modifyType = "";
        isModifyAccountDoctorModalOpen = false;

        _initAccountDoctorDetails();
        delete filters["name"];
    };

    const _initAccountDoctorDetails = (obj = {}) => {
        accountDoctorDetails = { ...obj };
    };

    const btnOptions = [
        {
            iconName: "addIcon",
            tooltip: "Add New Surgeon / Referrer",
            iFont: addIcon,
            iconFunction: () => _openModifyAccountDoctorModal("Create"),
            btnText: "Add",
        },
    ];
</script>

<div class="flex flex-col overflow-y-auto flex-1 gap-4 m-4">
    <CardLayout
        title="Surgeons / Referrers"
        {btnOptions}
        flex1
        enableCollapse={false}
    >
        <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
            {#if isGettingAccountDoctors}
                <PageLoading />
            {:else}
                <div
                    class={$isMobile
                        ? "overflow-x-auto overflow-y-visible"
                        : "overflow-y-auto absolute inset-0"}
                >
                    <SvelteTable
                        {columns}
                        rows={accountDoctors}
                        clickCellFunc={({ detail: { event, row, key } }) => {
                            handleClickCell(event, row, key);
                        }}
                    />
                </div>
            {/if}
        </div>

        <div class="flex flex-col px-4 py-3">
            <div class="flex flex-1 justify-between items-center">
                <div class="flex items-center gap-x-4">
                    <div
                        class="flex items-center {$isMobile
                            ? 'flex-col'
                            : 'flex-row'}"
                    >
                        <strong class="text-gray-600 text-sm"
                            >Table size:</strong
                        >
                        <select
                            class="select select-sm text-gray-600 {$isMobile
                                ? 'm-2'
                                : 'mx-2'}"
                            bind:value={pageSize}
                            on:change|preventDefault={() =>
                                onPageSizeChange(pageSize)}
                        >
                            {#each pageOptions as pageOption}
                                <option
                                    value={pageOption.amount}
                                    selected={pageOption.amount === pageSize}
                                >
                                    {pageOption.amount}</option
                                >
                            {/each}
                        </select>
                    </div>
                </div>

                {#if accountDoctors.length}
                    <Pagination
                        {page}
                        {pageSize}
                        {count}
                        on:onPageChange={onPageChange}
                    />
                {/if}
            </div>
        </div>
    </CardLayout>
</div>

<CreateSurgeonModal
    isCreateNewSurgeonModalOpen={isModifyAccountDoctorModalOpen}
    closeFunc={_closeModifyAccountDoctorModal}
    bind:filters
    bind:selectedSurgeon={accountDoctorDetails.searchResult}
    doctorId={accountId}
    existedOptions={accountDoctorSearchResults}
    reloadSurgeonDetails={async () =>
        await loadAccountDoctors(page * pageSize, pageSize)}
/>
<!-- <NotificationModal
    isModalOpen={isModifyAccountDoctorModalOpen && modifyType == "Create"}
    title="Add new surgeon/referrer"
    cancelText="Cancel"
    submitText={isModifyingAccountDoctor ? "Adding" : "Add"}
    isConfirming={isModifyingAccountDoctor}
    closeModal={_closeModifyAccountDoctorModal}
    confirmAction={modifyAccountDoctor}
    closeModalFunction={_closeModifyAccountDoctorModal}
>
    <div class="flex flex-1 flex-col gap-y-4">
        <div class="flex flex-1 justify-between gap-2">
            <SearchSelect
                bind:value={filters.state}
                title="State"
                wrapperClass="flex-1"
                inputClass={`bg-[#fff] force-w-full ${
                    isSearchingSurgeons ? "cursor-not-allowed" : ""
                }`}
                onChangeFunc={handleFilterSelect}
                isLong
                isDisabled={isSearchingSurgeons}
            >
                {#each AustralianStates as state}
                    <option
                        value={state.key.toUpperCase()}
                        selected={state.key == "vic"}
                    >
                        {state.name} - {state.key.toUpperCase()}
                    </option>
                {/each}
            </SearchSelect>
        </div>

        <div class="flex w-full">
            <Select
                bind:value={accountDoctorDetails.searchResult}
                loadOptions={handleDoctorSearch}
                items={accountDoctorSearchResults}
                itemId="uniqueId"
                label="displayName"
                debounceWait={SEARCH.DEBOUNCE_TIME}
                on:clear={clearSelectedSurgeon}
                loading={isSearchingSurgeons}
                noOptionsMessage="No Surgeons found"
                placeholder={isSearchingSurgeons ? "Loading..." : "Select"}
            />
        </div>

        <div class="flex w-full items-center gap-x-4">
            <input
                type="checkbox"
                id="searchCommonSurgeons"
                class="checkbox checkbox-secondary"
                checked={searchCommonSurgeons}
                on:click={(e) => {
                    e.stopPropagation();
                    searchCommonSurgeons = !searchCommonSurgeons;
                }}
            />
            <label for="searchCommonSurgeons" class="cursor-pointer">
                Filter for procedural specialties
            </label>
        </div>
    </div>
</NotificationModal> -->

<NotificationModal
    isModalOpen={modifyType == "Delete" && isModifyAccountDoctorModalOpen}
    title="Delete Surgeon"
    cancelText={isModifyingAccountDoctor ? "Deleting" : "Delete"}
    submitText="Cancel"
    isReverseBtns
    confirmButtonClass="btn-accent"
    isConfirming={isModifyingAccountDoctor}
    closeModal={modifyAccountDoctor}
    closeModalFunction={_closeModifyAccountDoctorModal}
    confirmAction={_closeModifyAccountDoctorModal}
>
    <div class="flex flex-col gap-y-1">
        <p>Delete selected surgeon?</p>
        <p>Surgeon name: {_getName(accountDoctorDetails)}</p>
        <p>Speciality: {getText(accountDoctorDetails.speciality)}</p>
    </div>
</NotificationModal>
